import { apiFetch } from './api.js';

/**
 * The LAUNCH-30 line - C-133.
 *
 * The website's offer page links to `/sign-up?offer=LAUNCH-30`. The sign-up
 * page says the discount is waiting, and so does the first visit to Buy
 * credits after the account exists. **Nothing applies the code**: the
 * customer still types it, and every rule the quote applies is unchanged.
 *
 * Mirrors `PUBLIC_OFFERS` on the server, which is the authority: the server
 * answers only about these, and says whether one is live today.
 */
export const KNOWN_OFFERS = ['LAUNCH-30'] as const;

export type KnownOffer = (typeof KNOWN_OFFERS)[number];

/** A listed offer the server says a buyer could use today. */
export interface LiveOffer {
  code: KnownOffer;
  percentOff: number;
  /** The coupon's own expiry, an instant. Null when it has none. */
  endsAt: string | null;
}

/** The listed offer `?offer=` names on `search`, in any case, or null. */
export function offerFrom(search: string): KnownOffer | null {
  const wanted = new URLSearchParams(search).get('offer')?.trim().toUpperCase();
  return KNOWN_OFFERS.find((code) => code === wanted) ?? null;
}

/**
 * Where an offer waits between the address it arrived on and the first Buy
 * credits visit. **The offer code only**: none of the ad parameters, which
 * still live in the address and the one sign-up request (C-132). It is not
 * reported anywhere, and a browser that blocks storage just loses the line.
 */
const STORAGE_KEY = 'entitylinks.offer';

export function rememberOffer(code: KnownOffer): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Storage blocked or full: the line is a convenience, not a record.
  }
}

/** The remembered offer, if there is one. Does not forget it. */
export function rememberedOffer(): KnownOffer | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return KNOWN_OFFERS.find((code) => code === stored) ?? null;
  } catch {
    return null;
  }
}

/** Forgets it, once Buy credits has shown it. */
export function forgetOffer(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do.
  }
}

/** The server's answer for `code`: the offer if it is live, otherwise null. Never throws. */
export async function fetchLiveOffer(code: KnownOffer): Promise<LiveOffer | null> {
  try {
    const response = await apiFetch<{ offer: LiveOffer | null }>(
      `/api/offers/${encodeURIComponent(code)}`,
    );
    return response.offer;
  } catch {
    // The line is a nicety. A failed check shows nothing rather than an error.
    return null;
  }
}

/**
 * The end date in words, on the US Pacific day: "31 October" (C-133). A code
 * set to run to 23:59 Pacific is stored as 06:59 the next morning in UTC, so
 * printing the UTC day would say it ends a day late. Null when there is no
 * expiry.
 */
export function offerEndsInWords(endsAt: string | null): string | null {
  if (endsAt === null) return null;
  const instant = new Date(endsAt);
  if (Number.isNaN(instant.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: 'America/Los_Angeles',
    day: 'numeric',
    month: 'long',
  }).format(instant);
}
